import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { DashboardAnimalData } from '../../models/dashboard';
import { AnimalType } from '../../models/animal';
import DashboardAnimalDataChart, { VaccineLegend, getVaccineSeries } from '../DashboardAnimalDataChart';
import { Card } from '../../elements/ui/card';

import SowIco from '../../static/sow01.jpg';
import GiltIco from '../../static/gilt01.jpg';
import PigletIco from '../../static/piglet01.jpg';
import FatteningIco from '../../static/fatt01.jpg';
import CowIco from '../../static/cow01.jpg';
import GoatIco from '../../static/goat01.jpg';
import CalfIco from '../../static/calf01.jpg';

interface DashboardAnimalCardProps {
  animalData: DashboardAnimalData;
}

const animalTypeImages: Partial<Record<AnimalType, string>> = {
  [AnimalType.Sow]: SowIco,
  [AnimalType.Gilt]: GiltIco,
  [AnimalType.Piglet]: PigletIco,
  [AnimalType.Fattening]: FatteningIco,
  [AnimalType.Goat]: GoatIco,
  [AnimalType.Cow]: CowIco,
  [AnimalType.Calf]: CalfIco
};

const DashboardAnimalCard = ({ animalData }: DashboardAnimalCardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const image = animalTypeImages[animalData.animalType];
  const series = getVaccineSeries(animalData);

  return (
    <Card className="flex h-full flex-col">
      {/* The daily dose is the card's headline number, so it gets stat-card treatment - the same
          type scale as the piglet cards above, which keeps the two rows reading as one dashboard. */}
      <div className="flex items-start justify-between gap-3 p-4">
        <div className="flex min-w-0 items-center gap-3">
          {image && (
            <img alt="" src={image} className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-gray-200" />
          )}
          <h3 className="truncate text-sm font-semibold text-gray-900">
            {t(AnimalType[animalData.animalType].toString())}
          </h3>
        </div>

        <div className="shrink-0 text-right">
          <p className="text-[11px] font-medium text-gray-500">{t('f-dashboard-daily-dose-label')}</p>
          <p className="text-2xl font-semibold leading-tight tracking-tight text-gray-900 tabular-nums">
            {(animalData.dailyDose ?? 0).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="h-36 w-full px-2">
        <DashboardAnimalDataChart data={animalData} />
      </div>

      {series.some((entry) => entry.name) && (
        <div className="px-4 pt-3 pb-4">
          <VaccineLegend series={series} />
        </div>
      )}

      {/* Every native button style is reset explicitly - this project loads Tailwind's theme and utilities
          without preflight, so a bare <button> keeps the browser's grey face and border. */}
      <div className="mt-auto border-t border-gray-100">
        <button
          type="button"
          onClick={() => navigate(`/strategy/injections/${animalData.animalType}`)}
          className="flex w-full cursor-pointer appearance-none items-center justify-center gap-1 rounded-b-lg border-0 bg-transparent px-4 py-3 text-xs font-medium text-indigo-600 transition-colors hover:bg-gray-50 hover:text-indigo-700"
        >
          <span className="truncate">{t('f-dashboard-view-vaccination-medication')}</span>
          <span aria-hidden="true" className="text-sm leading-none">
            ›
          </span>
        </button>
      </div>
    </Card>
  );
};

export default DashboardAnimalCard;
